import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Warning } from '@phosphor-icons/react';
import { useEmployeeSnapshot } from '@/modules/service-desk/shared/employee';
import { splitName } from '@/modules/service-desk/shared/application-utils';
import { translatedResolver } from '@/modules/service-desk/shared/form-resolver';
import { LookupItem, useServiceDeskDepartments } from '@/modules/service-desk/shared/lookups';
import { ApplyFormPage } from '@/modules/service-desk/shared/components/apply-form-page';
import {
  FieldGrid,
  PhoneField,
  SelectField,
  TextField,
} from '@/modules/service-desk/shared/components/form-fields';
import { useByodApplication, useSubmitByod } from '../../hooks/use-byod';
import { isPastApplyCutoff, readAccountNumber } from '../../utils/byod-rules';
import { ByodApplyValues, byodApplySchema } from '../../schemas/byod-apply-form-schema';

const EMPTY_VALUES: ByodApplyValues = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  employeeId: '',
  designation: '',
  bankAccount: '',
  departmentId: '',
  deviceModel: '',
  computerProcessor: '',
  motherboard: '',
  ramCapacity: '',
  monitorSpec: '',
  storageSpec: '',
};

export const ByodApplyForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('id') ?? undefined;

  const { snapshot, isLoading: isSnapshotLoading } = useEmployeeSnapshot();
  const { data: existing, isLoading: isExistingLoading } = useByodApplication(applicationId);
  const { data: departments = [] as LookupItem[], isLoading: isDepartmentsLoading } =
    useServiceDeskDepartments();
  const { mutateAsync: submit, isPending } = useSubmitByod();

  const form = useForm<ByodApplyValues>({
    resolver: translatedResolver(byodApplySchema, t),
    defaultValues: EMPTY_VALUES,
  });

  // Editing loads the saved application; a new application is pre-filled from the employee profile.
  useEffect(() => {
    if (applicationId) {
      if (!existing) return;
      const { firstName, lastName } = splitName(existing.FullName);
      form.reset({
        firstName,
        lastName,
        email: existing.EmailAddress ?? '',
        phoneNumber: existing.MobileNumber ?? '',
        employeeId: existing.EmployeeId ?? '',
        designation: existing.Designation ?? '',
        bankAccount: readAccountNumber(existing.BankInfoJson),
        departmentId: existing.RequestDepartmentId ?? '',
        deviceModel: existing.DeviceModel ?? '',
        computerProcessor: existing.ComputerProcessor ?? '',
        motherboard: existing.Motherboard ?? '',
        ramCapacity: existing.RamCapacity ?? '',
        monitorSpec: existing.MonitorDetails ?? '',
        storageSpec: existing.HddSsdDetails ?? '',
      });
      return;
    }
    if (!snapshot) return;
    const { firstName, lastName } = splitName(snapshot.displayName);
    form.reset({
      ...EMPTY_VALUES,
      firstName,
      lastName,
      email: snapshot.email,
      phoneNumber: snapshot.phoneNumber,
      employeeId: snapshot.employeeId,
      designation: snapshot.designation,
      departmentId: snapshot.departmentId,
    });
  }, [applicationId, existing, snapshot, form]);

  const goBack = () => navigate('/hr/service-desk/byod');

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await submit({ existing: existing ?? undefined, values });
      goBack();
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  });

  const departmentOptions = departments.map((department: LookupItem) => ({
    value: department.ItemId,
    label: department.Title,
  }));
  // A legacy record's department was stored as free text (BusinessUnitName) with no id link, so an
  // edited application may load with an empty departmentId even though it has a prior value on file.
  const legacyDepartmentName =
    existing && !existing.RequestDepartmentId
      ? existing.BusinessUnitName || existing.RequestDepartmentName
      : '';

  return (
    <ApplyFormPage
      form={form}
      titleKey="BYOD_APPLICATION_FORM"
      isSubmitting={isPending}
      isEdit={Boolean(applicationId)}
      isLoading={applicationId ? isExistingLoading : isSnapshotLoading}
      // A new application needs the applicant's profile (prefill and department); an edit does not.
      submitDisabled={!applicationId && !snapshot}
      onCancel={goBack}
      onSubmit={handleSubmit}
      notice={
        isPastApplyCutoff() && (
          <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
            <Warning className="mt-0.5 size-4 shrink-0" />
            <span>{t('BYOD_APPLY_PAST_CUTOFF_NOTE')}</span>
          </div>
        )
      }
    >
      <FormSection>
        <FieldGrid>
          <TextField control={form.control} name="firstName" required label={t('FIRST_NAME')} />
          <TextField control={form.control} name="lastName" required label={t('LAST_NAME')} />
          <TextField control={form.control} name="email" required type="email" label={t('EMAIL')} />
          <PhoneField
            control={form.control}
            name="phoneNumber"
            required
            label={t('MOBILE_NUMBER')}
          />
          <TextField control={form.control} name="employeeId" required label={t('EMPLOYEE_ID')} />
          <TextField control={form.control} name="designation" required label={t('DESIGNATION')} />
          <SelectField
            control={form.control}
            name="departmentId"
            required
            label={t('DEPARTMENT')}
            options={departmentOptions}
            isLoading={isDepartmentsLoading}
            description={
              legacyDepartmentName
                ? `${t('PREVIOUSLY_RECORDED_AS')}: ${legacyDepartmentName}`
                : undefined
            }
          />
          <TextField
            control={form.control}
            name="bankAccount"
            required
            label={t('BANK_ACCOUNT')}
            placeholder={t('TYPE_BANK_ACCOUNT')}
            description={t('BANK_ACCOUNT_NOTE')}
          />
        </FieldGrid>
      </FormSection>

      <FormSection title={t('COMPUTER_SPECS')}>
        <FieldGrid>
          <TextField
            control={form.control}
            name="deviceModel"
            required
            label={t('DEVICE_MODEL')}
            placeholder={t('TYPE_DEVICE_MODEL')}
          />
          <TextField
            control={form.control}
            name="computerProcessor"
            required
            label={t('COMPUTER_PROCESSOR')}
            placeholder={t('TYPE_PROCESSOR')}
          />
          <TextField
            control={form.control}
            name="motherboard"
            required
            label={t('MOTHERBOARD')}
            placeholder={t('TYPE_MOTHERBOARD')}
          />
          <TextField
            control={form.control}
            name="ramCapacity"
            required
            label={t('RAM_CAPACITY')}
            placeholder={t('TYPE_RAM_CAPACITY')}
          />
          <TextField
            control={form.control}
            name="monitorSpec"
            required
            label={t('MONITOR_SPEC')}
            placeholder={t('TYPE_MONITOR_SPEC')}
          />
          <TextField
            control={form.control}
            name="storageSpec"
            required
            label={t('STORAGE_SPEC')}
            placeholder={t('TYPE_STORAGE_SPEC')}
          />
        </FieldGrid>
      </FormSection>
    </ApplyFormPage>
  );
};

const FormSection = ({ title, children }: { title?: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-3">
    {title && <h2 className="text-sm font-semibold">{title}</h2>}
    {children}
  </section>
);
